import {} from 'zod';
/**
 * Field-level redaction (slice-00 §8 layer 4, spec §10.5).
 *
 * The declarative map lives **on the schema itself** rather than in a table
 * keyed by DTO name. That choice is the whole design:
 *
 *   - A name-keyed map only redacts where somebody remembered to name the DTO.
 *     A single-resource read gets it; the list endpoint added six months later
 *     returns the same DTO under a different route and is missed. That is not
 *     hypothetical — it is the ordinary shape of this bug.
 *   - Marking the field where it is declared means redaction follows the field
 *     wherever the field goes: inside an array, inside a nested relation,
 *     inside a discriminated union arm. The walker below is driven by the
 *     schema, so there is no route it can be reached through that it is not
 *     covered on.
 *
 * The restriction is attached out of band, in a WeakMap, so it never appears in
 * the parsed type, never crosses the wire, and cannot be read by a client.
 */
const RESTRICTIONS = new WeakMap();
/**
 * Mark a field as visible only to callers holding `permission`.
 *
 * Returns the same schema instance, tagged. Call it on a freshly constructed
 * schema — `restricted(z.string().nullable(), 'admin:users')` — so the tag
 * cannot leak onto a shared schema used elsewhere unrestricted.
 */
export function restricted(schema, permission) {
    RESTRICTIONS.set(schema, permission);
    return schema;
}
export function restrictionOf(schema) {
    return RESTRICTIONS.get(schema);
}
/**
 * The restricted field names of an object schema.
 *
 * This is the *same* map §8's redaction reads, asked a different question. §9
 * needs to know which values to hash before they reach `audit_log.before` /
 * `after`, and "fields flagged sensitive" is exactly "fields a caller needs a
 * permission to see".
 *
 * Deliberately not a second list. Two lists of sensitive fields drift, and the
 * one that drifts is always the audit one — redaction is visible the moment it
 * is wrong, because a user complains that a field vanished, whereas a missed
 * audit hash is invisible until a breach report. One declaration, two readers.
 */
export function sensitiveFields(schema) {
    const def = schema._def;
    if (def.typeName !== 'ZodObject')
        return [];
    const shape = schema.shape;
    return Object.entries(shape)
        .filter(([, fieldSchema]) => restrictionOf(fieldSchema) !== undefined)
        .map(([name]) => name)
        .sort();
}
/**
 * Strip every field the caller lacks the permission for.
 *
 * Returns a redacted copy; the input is never mutated. Fields are **removed**,
 * not nulled — a null says "this person has no phone number", which is a
 * different and wrong statement.
 *
 * Unknown schema node types are returned unchanged, which is safe because only
 * container types can hide a restricted field and every container Zod offers is
 * handled below. `redaction.spec.ts` pins that with a deliberately awkward
 * schema.
 */
export function redact(schema, value, held) {
    return walk(schema, value, new Set(held));
}
function walk(schema, value, held) {
    if (value === null || value === undefined)
        return value;
    const def = schema._def;
    switch (def.typeName) {
        // Wrappers — unwrap and carry on. A restriction on the wrapper is checked
        // by the caller (the object walker), so this only has to descend.
        case 'ZodOptional':
        case 'ZodNullable':
        case 'ZodReadonly':
            return walk(schema.unwrap(), value, held);
        case 'ZodDefault':
        case 'ZodCatch':
            return walk(def.innerType, value, held);
        case 'ZodEffects':
            return walk(def.schema, value, held);
        case 'ZodLazy':
            return walk(def.getter(), value, held);
        case 'ZodArray': {
            if (!Array.isArray(value))
                return value;
            // The list case. Nothing special is done for it — an array is just a
            // container, and its element schema carries the same restrictions the
            // single-resource read does.
            return value.map((item) => walk(def.type, item, held));
        }
        case 'ZodRecord': {
            if (typeof value !== 'object')
                return value;
            const valueSchema = def.valueType;
            const out = {};
            for (const [key, item] of Object.entries(value)) {
                out[key] = walk(valueSchema, item, held);
            }
            return out;
        }
        case 'ZodObject': {
            if (typeof value !== 'object' || Array.isArray(value))
                return value;
            const shape = schema.shape;
            const out = {};
            for (const [key, item] of Object.entries(value)) {
                const fieldSchema = shape[key];
                // A key with no schema is not described by the contract. Dropped rather
                // than passed through: an undeclared field is a field nobody decided
                // was safe to send.
                if (!fieldSchema)
                    continue;
                const required = restrictionOf(fieldSchema);
                if (required && !held.has(required))
                    continue;
                out[key] = walk(fieldSchema, item, held);
            }
            return out;
        }
        case 'ZodUnion': {
            // Redact against whichever arm actually parses. Guessing would be worse:
            // picking the wrong arm could leave a restricted field untouched because
            // the arm it was declared on was not the one consulted.
            const options = def.options;
            const match = options.find((option) => option.safeParse(value).success);
            return match ? walk(match, value, held) : value;
        }
        case 'ZodDiscriminatedUnion': {
            const discriminator = def.discriminator;
            const options = def.options;
            const tag = value[discriminator];
            const match = options.find((option) => option.shape[discriminator]?.safeParse(tag)
                .success);
            return match ? walk(match, value, held) : value;
        }
        default:
            // Primitives, enums, literals, dates. Nothing to descend into and nothing
            // that can hide a restricted field.
            return value;
    }
}
//# sourceMappingURL=redaction.js.map